'use client'

import { useState, useCallback, useRef } from 'react'
import { genUploader } from 'uploadthing/client'
import { OurFileRouter } from '@/app/api/uploadthing/core'
import { ContentFile, createUploadingFile, createCompletedFile } from '@/lib/types/file-types'
import { toast } from 'sonner'
import { processFilesForUpload, getConversionCount } from '@/lib/files'

const { createUpload } = genUploader<OurFileRouter>()

interface UseSimpleFileUploadOptions {
    endpoint?: keyof OurFileRouter
    maxFiles?: number
    onUploadStart?: (file: ContentFile) => void
    onUploadProgress?: (file: ContentFile) => void  
    onUploadComplete?: (file: ContentFile) => void
    onUploadError?: (file: ContentFile, error: Error) => void
    onAllUploadsComplete?: (files: ContentFile[]) => void
}

interface UseSimpleFileUploadReturn {
    // File browser
    openFileBrowser: () => void
    
    // Drag & drop
    dragProps: {
        onDrop: (e: React.DragEvent) => void
        onDragOver: (e: React.DragEvent) => void
        onDragLeave: (e: React.DragEvent) => void
        isDragActive: boolean
    }
    
    // State
    uploadingFiles: ContentFile[]
    isUploading: boolean
    
    // Manual file addition
    uploadFiles: (files: File[]) => void
    
    // Clear files
    clearFiles: () => void
}

export function useSimpleFileUpload({
    endpoint = 'fileUploader',
    maxFiles = 10,
    onUploadStart,
    onUploadProgress,
    onUploadComplete,
    onUploadError,
    onAllUploadsComplete
}: UseSimpleFileUploadOptions = {}): UseSimpleFileUploadReturn {
    
    const [uploadingFiles, setUploadingFiles] = useState<ContentFile[]>([])
    const [isUploading, setIsUploading] = useState(false)
    const [isDragActive, setIsDragActive] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)
    
    // Create hidden file input if it doesn't exist
    const createFileInput = useCallback(() => {
        if (!fileInputRef.current) {
            const input = document.createElement('input')
            input.type = 'file'
            input.multiple = true
            input.style.display = 'none'
            input.onchange = (e) => {
                const files = Array.from((e.target as HTMLInputElement).files || [])
                if (files.length > 0) {
                    uploadFiles(files)
                }
                // Reset input to allow same files again
                input.value = ''
            }
            document.body.appendChild(input)
            fileInputRef.current = input
        }
        return fileInputRef.current
    }, [])
    
    // Open file browser
    const openFileBrowser = useCallback(() => {
        const input = createFileInput()
        input.click()
    }, [createFileInput])
    
    // Error mapping function to handle server errors gracefully
    const handleUploadError = useCallback((error: any, fileName: string) => {
        console.log('handleUploadError', error, fileName)
        const errorMessage = error?.message || 'Upload failed'
        
        // Map server errors to user-friendly messages and appropriate toasts
        if (errorMessage.includes('TOO_LARGE') || errorMessage.includes('too large') || errorMessage.includes('exceeds')) {
            toast.error('File Too Large', {
                description: `"${fileName}" exceeds the maximum file size limit.`,
                duration: 5000
            })
            return 'File exceeds size limit'
        }
        
        if (errorMessage.includes('BAD_REQUEST') || errorMessage.includes('not supported') || errorMessage.includes('invalid') || errorMessage.includes('File type') || errorMessage.includes('cannot be processed')) {
            toast.error('Unsupported File Type', {
                description: errorMessage.includes('File type') ? errorMessage : `"${fileName}" is not a supported file type.`,
                duration: 5000
            })
            return 'Unsupported file type'
        }
        
        if (errorMessage.includes('TOO_MANY_FILES') || errorMessage.includes('too many')) {
            toast.error('Too Many Files', {
                description: `Maximum ${maxFiles} files allowed at once.`,
                duration: 5000
            })
            return 'Too many files'
        }
        
        if (errorMessage.includes('FORBIDDEN') || errorMessage.includes('sign up') || errorMessage.includes('log in')) {
            toast.error('Authentication Required', {
                description: 'Please sign up or log in to upload files.',
                duration: 6000
            })
            return 'Authentication required'
        }
        
        if (errorMessage.includes('network') || errorMessage.includes('timeout')) {
            toast.error('Network Error', {
                description: 'Please check your connection and try again.',
                duration: 5000
            })
            return 'Network error'
        }
        
        // Default error
        toast.error('Upload Failed', {
            description: 'Please try again or contact support if the issue persists.',
            duration: 5000
        })
        return 'Upload failed'
    }, [maxFiles])

    // Upload files function  
    const uploadFiles = useCallback(async (files: File[]) => {
        if (files.length === 0) return
        
        // Convert HEIC/HEIF files to JPEG before proceeding
        console.log('Processing files for upload...')
        const processedFiles = await processFilesForUpload(files)
        
        if (processedFiles.length === 0) {
            toast.error('No valid files to upload')
            return
        }
        
        // Show conversion summary if any files were converted
        const convertedCount = getConversionCount(files, processedFiles)
        
        if (convertedCount > 0) {
            // toast.success('Files Converted', {
            //     description: `${convertedCount} HEIC/HEIF file${convertedCount > 1 ? 's' : ''} converted to JPEG.`,
            //     duration: 4000
            // })
        }
        
        // Check max files limit (basic client-side UX check)
        const currentCount = uploadingFiles.filter(f => f.uploadState?.status !== 'error').length
        const remainingSlots = maxFiles - currentCount
        
        if (remainingSlots <= 0) {
            toast.error(`Maximum ${maxFiles} files allowed`)
            return
        }
        
        const filesToUpload = processedFiles.slice(0, remainingSlots)
        if (filesToUpload.length < processedFiles.length) {
            toast.warning(`Only uploading first ${filesToUpload.length} files (max ${maxFiles})`)
        }
        
        // Create uploading file objects using unified ContentFile
        const newUploadingFiles: ContentFile[] = filesToUpload.map(file => createUploadingFile(file))
        
        setUploadingFiles(prev => [...prev, ...newUploadingFiles])
        setIsUploading(true)
        
        // Call onUploadStart for each file
        newUploadingFiles.forEach(file => onUploadStart?.(file))
        
        // Track successful uploads
        const completedFiles: ContentFile[] = []
        
        // Upload each file
        const uploadPromises = newUploadingFiles.map(async (uploadingFile) => {
            try {
                const { done } = await createUpload(endpoint, {
                    files: [uploadingFile.uploadState!.file!],
                    onUploadProgress: ({ progress }) => {
                        const updatedFile = {
                            ...uploadingFile,
                            uploadState: {
                                ...uploadingFile.uploadState!,
                                progress: Math.round(progress),
                                status: 'uploading' as const
                            }
                        }
                        
                        setUploadingFiles(prev => 
                            prev.map(f => 
                                f.id === uploadingFile.id ? updatedFile : f
                            )
                        )
                        
                        onUploadProgress?.(updatedFile)
                    }
                })
                
                const result = await done()
                console.debug('UploadThing done() result', result)

                // Validate server response shape before creating completed file
                const serverData = result?.[0]?.serverData
                if (!serverData) {
                    console.error('Missing serverData in upload result', result)
                    throw new Error('Invalid server response from upload handler')
                }

                // If server reported an error payload, mark this file as error and surface message
                if (serverData?.error) {
                    const errorMessage = typeof serverData.error === 'string' ? serverData.error : 'Upload failed'
                    const errorFile = {
                        ...uploadingFile,
                        uploadState: {
                            ...uploadingFile.uploadState!,
                            status: 'error' as const,
                            error: errorMessage
                        }
                    }
                    setUploadingFiles(prev => 
                        prev.map(f => 
                            f.id === uploadingFile.id ? errorFile : f
                        )
                    )
                    onUploadError?.(errorFile, new Error(errorMessage))
                    return
                }

                // Create completed file but preserve the original file's ID for form updates
                const completedFile: ContentFile = {
                    ...createCompletedFile(serverData),
                    id: uploadingFile.id,  // Keep original ID for form matching
                    uploadState: {
                        status: 'complete',
                        progress: 100
                    }
                }
                
                // Update status to complete
                setUploadingFiles(prev => 
                    prev.map(f => 
                        f.id === uploadingFile.id ? completedFile : f
                    )
                )
                
                completedFiles.push(completedFile)
                onUploadComplete?.(completedFile)
                
            } catch (error) {
                // Use the centralized error handler for consistent error mapping
                const userFriendlyError = handleUploadError(error, uploadingFile.name)
                
                const errorFile = {
                    ...uploadingFile,
                    uploadState: {
                        ...uploadingFile.uploadState!,
                        status: 'error' as const,
                        error: userFriendlyError
                    }
                }
                
                // Update file status to error
                setUploadingFiles(prev => 
                    prev.map(f => 
                        f.id === uploadingFile.id ? errorFile : f
                    )
                )
                
                onUploadError?.(errorFile, error instanceof Error ? error : new Error(userFriendlyError))
            }
        })
        
        // Wait for all uploads to complete
        await Promise.allSettled(uploadPromises)
        
        setIsUploading(false)
        
        // Call completion callback with successful files
        if (completedFiles.length > 0) {
            onAllUploadsComplete?.(completedFiles)
        }
        
        // Auto-clear successful uploads after a delay
        setTimeout(() => {
            setUploadingFiles(prev => prev.filter(f => f.uploadState?.status === 'error'))
        }, 2000)
        
    }, [endpoint, maxFiles, uploadingFiles, onUploadStart, onUploadProgress, onUploadComplete, onUploadError, onAllUploadsComplete, handleUploadError])
    
    // Clear files function
    const clearFiles = useCallback(() => {
        setUploadingFiles([])
    }, [])
    
    // Drag and drop handlers
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (!isDragActive) {
            setIsDragActive(true)
        }
    }, [isDragActive])
    
    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        // Only set inactive if we're leaving the drag area completely
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragActive(false)
        }
    }, [])
    
    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
        setIsDragActive(false)
        
        const droppedFiles = Array.from(e.dataTransfer.files)
        if (droppedFiles.length > 0) {
            uploadFiles(droppedFiles)
        }
    }, [uploadFiles])
    
    return {
        openFileBrowser,
        dragProps: {
            onDrop: handleDrop,
            onDragOver: handleDragOver,
            onDragLeave: handleDragLeave,
            isDragActive
        },
        uploadingFiles,
        isUploading,
        uploadFiles,
        clearFiles
    }
} 